/**
 * Affiliate program — the frontend mirror of the wire contract.
 *
 * Source of truth: `backend/modules/affiliate/schemas.py` +
 * `docs/affiliate-module/affiliate-api-contract.md`. A change lands in all three
 * artefacts in one pass or it is not a change.
 *
 * CONSTANTS ONLY. No shape assertions live here: a client-side `assertShape` is a second
 * copy of the contract that drifts silently the day the server adds a field, and the
 * loaders already have one honest answer for a payload they cannot read
 * (`payloadShapeError` + `LoadFailure`). The fixtures in `./fixtures.js` are for TESTS
 * and are imported by no component — there is no runtime switch between mock and real.
 */

// The settlement currency is re-exported, never re-spelled. `gateway/contract.js` is the
// ONE compile-time mirror of `schemas.DEFAULT_SETTLEMENT_CURRENCY` (and the one file the
// currency-literal ratchet allows to hold it); an affiliate fixture that wrote "usd" of
// its own would be the second place to forget on the day the operator's legal entity
// changes.
import { SETTLEMENT_CURRENCY } from "../gateway/contract.js";

export { SETTLEMENT_CURRENCY };

// ── closed sets (mirror modules/affiliate/schemas.py) ──────────────────────────

/** Which money funds the commission. Ordered; the backend declares a tuple. */
export const AFFILIATE_KINDS = Object.freeze(["saas", "marketplace"]);

/** An affiliate account may earn, or it may not. There is no third state. */
export const ACCOUNT_STATES = Object.freeze(["active", "disabled"]);

/**
 * The life of one referred practice. `expired` earned its end (the window elapsed);
 * `voided` did not (the link was withdrawn), and the two must not render alike.
 */
export const REFERRAL_STATES = Object.freeze([
  "linked", "active", "expired", "voided",
]);

/**
 * A commission is accrued the instant its ledger group is durable and from then on only
 * ever moves toward having been given back. There is deliberately NO `paid`: payment is
 * the Gateway Wallet's withdrawal rail, and a second collection claiming to know whether
 * money left is the v0 defect this module replaces.
 */
export const COMMISSION_STATES = Object.freeze([
  "accrued", "partially_reversed", "reversed",
]);

/** What the commission is keyed to: a gateway payment row, or a subscription invoice. */
export const COMMISSION_SOURCE_TYPES = Object.freeze(["payment", "subscription"]);

/**
 * Where a rate IN FORCE comes from, per slot of `GET /super-admin/affiliate/rules#inForce`:
 * an operator-authored rule, or the platform default when no rule is open for the slot.
 * `ruleId` is non-null exactly when the source is `rule`.
 */
export const RATE_SOURCES = Object.freeze(["default", "rule"]);

/**
 * The refusal vocabulary — CLOSED. `errorText(code, t)` maps a member to
 * `aff.error.<CODE>` and an unknown one to `aff.error.UNKNOWN`; `detail.message` is
 * English and is never rendered.
 *
 * `REAUTH_REQUIRED` and `RATE_LIMITED` are not this module's — they are raised by the
 * gateway's step-up gate and by the rate limiter — and they are here because a client of
 * these paths must handle them.
 */
export const AFFILIATE_ERROR_CODES = Object.freeze([
  "AFFILIATE_PROGRAM_DISABLED",
  "AFFILIATE_CODE_UNKNOWN",
  "AFFILIATE_CODE_TAKEN",
  "AFFILIATE_CODE_INVALID",
  "AFFILIATE_CODE_ALREADY_CHANGED",
  "AFFILIATE_ACCOUNT_DISABLED",
  "AFFILIATE_COMMISSION_NOT_FOUND",
  "AFFILIATE_COMMISSION_ALREADY_REVERSED",
  "AFFILIATE_RULE_INVALID",
  "REAUTH_REQUIRED",
  "RATE_LIMITED",
]);

/** Mirrors `schemas.CODE_PATTERN`. Lowercase alphanumeric, 4–16. */
export const CODE_PATTERN = /^[a-z0-9]{4,16}$/;

/**
 * Mirrors `tickets.TICKET_RE`: the server-signed referral ticket a click is answered with
 * (`r1.<code>.<issued base36>.<22-char signature>`). The browser never verifies one — it
 * only tells a ticket from a code in a `?ref=` by shape. Pinned against the backend
 * regex by `test_affiliate_frontend_constants_pinned.py`.
 */
export const TICKET_PATTERN = /^r1\.[a-z0-9]{4,16}\.[0-9a-z]{1,10}\.[A-Za-z0-9_-]{22}$/;

/** `localStorage` key holding `{ticket, expiresAt}` — see `lib/affiliateRef.js`. */
export const REF_STORAGE_KEY = "solunary_ref";

/** The query parameter a share link carries. */
export const REF_QUERY_PARAM = "ref";

// ── ledger vocabulary (read-only for the SPA) ──────────────────────────────────

/** Mirrors `schemas.ACCOUNT_PAYABLE` / `ACCOUNT_EXPENSE` — the two accounts an affiliate
 * commission moves. The super-admin trial-balance panel renders them by name. */
export const AFFILIATE_LEDGER_ACCOUNTS = Object.freeze([
  "affiliate_payable", "platform_affiliate_expense",
]);

/** Mirrors `schemas.LEDGER_PURPOSES`, in the order a commission can walk them. */
export const AFFILIATE_LEDGER_PURPOSES = Object.freeze([
  "affiliate_commission",
  "affiliate_commission_reversed",
  "affiliate_commission_reinstated",
]);

// ── API paths ──────────────────────────────────────────────────────────────────

/**
 * Every path is RELATIVE to the axios `baseURL`, which already ends in `/api`
 * (`lib/api.js`: ``const API = `${process.env.REACT_APP_BACKEND_URL}/api` ``). A path
 * written with its own `/api` prefix would reach `/api/api/...`.
 *
 * Parameterised paths are functions, so no caller builds one by string concatenation and
 * no id reaches a URL unencoded.
 */
export const AFFILIATE_PATHS = Object.freeze({
  // public — no auth, tenant resolved by Host
  clicks: "/affiliate/clicks",

  // tenant admin — owner session
  tenant: "/tenant/affiliate",
  tenantCode: "/tenant/affiliate/code",
  tenantReferrals: "/tenant/affiliate/referrals",
  tenantCommissions: "/tenant/affiliate/commissions",

  // super admin — finance.read / finance.write
  adminProgram: "/super-admin/affiliate/program",
  adminRules: "/super-admin/affiliate/rules",
  adminOverview: "/super-admin/affiliate/overview",
  adminAffiliates: "/super-admin/affiliate/affiliates",
  adminAffiliate: (tenantId) =>
    `/super-admin/affiliate/affiliates/${encodeURIComponent(tenantId)}`,
  adminAffiliateStatus: (tenantId) =>
    `/super-admin/affiliate/affiliates/${encodeURIComponent(tenantId)}/status`,
  adminCommissions: "/super-admin/affiliate/commissions",
  adminCommissionReverse: (commissionId) =>
    `/super-admin/affiliate/commissions/${encodeURIComponent(commissionId)}/reverse`,
  adminLegacyCommissions: "/super-admin/affiliate/legacy-commissions",

  // The step-up mint. It is the GATEWAY's, not a second one: one primitive, one
  // collection, one audit trail. `gateway/api.js#stepUpHeaders` builds the header.
  reauth: "/super-admin/gateway/reauth",
});

/** The header a step-up-gated write carries. Mirrors `require_stepup`. */
export const STEPUP_HEADER = "X-Stepup-Token";

// ── pagination ─────────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

/**
 * The ONE list query builder. The server clamps `pageSize` to 1..200 in the repository
 * and answers `{rows, total, page, pageSize}` with the values it actually applied, so a
 * caller reads the page it got back and never assumes the page it asked for.
 *
 * @param {number} [page]
 * @param {number} [pageSize]
 * @returns {{page: number, pageSize: number}}
 */
export const pageQuery = (page = 1, pageSize = DEFAULT_PAGE_SIZE) => ({
  page: Math.max(1, Number(page) || 1),
  pageSize: Math.max(1, Math.min(MAX_PAGE_SIZE, Number(pageSize) || DEFAULT_PAGE_SIZE)),
});

export default {
  SETTLEMENT_CURRENCY,
  AFFILIATE_KINDS,
  ACCOUNT_STATES,
  REFERRAL_STATES,
  COMMISSION_STATES,
  COMMISSION_SOURCE_TYPES,
  RATE_SOURCES,
  AFFILIATE_ERROR_CODES,
  AFFILIATE_LEDGER_ACCOUNTS,
  AFFILIATE_LEDGER_PURPOSES,
  AFFILIATE_PATHS,
  CODE_PATTERN,
  TICKET_PATTERN,
  REF_STORAGE_KEY,
  REF_QUERY_PARAM,
  STEPUP_HEADER,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  pageQuery,
};
